import { db, auth } from '@/firebase/config.js'
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  writeBatch
} from 'firebase/firestore'

const SCORECARD_MARKERS_COLLECTION = 'scorecard_markers'
const SCORECARD_OPENED_COLLECTION = 'scorecard_opened'

/**
 * Format month/year as YYYY-MM for scorecard marker/opened queries
 * @param {number} month - Month (0-11)
 * @param {number} year
 * @returns {string}
 */
export const formatScorecardYearMonth = (month, year) => {
  return `${year}-${String(month + 1).padStart(2, '0')}`
}

/**
 * Get marked exam result IDs for a given month (examDate yearMonth)
 * @param {number} month - Month (0-11)
 * @param {number} year
 * @returns {Promise<string[]>}
 */
export const getScorecardMarkersByMonth = async (month, year) => {
  const yearMonth = formatScorecardYearMonth(month, year)
  const markedIds = []

  try {
    const q = query(
      collection(db, SCORECARD_MARKERS_COLLECTION),
      where('yearMonth', '==', yearMonth)
    )
    const querySnapshot = await getDocs(q)
    querySnapshot.forEach((markerDoc) => {
      markedIds.push(markerDoc.id)
    })
  } catch (error) {
    console.error('Error getting scorecard markers:', error)
    throw error
  }

  return markedIds
}

/**
 * Mark a single exam result (shared across admins)
 * @param {string} resultId
 * @param {string} yearMonth - YYYY-MM
 * @returns {Promise<void>}
 */
export const markScorecardResult = async (resultId, yearMonth) => {
  if (!resultId || !yearMonth) return

  const adminUser = auth.currentUser
  await setDoc(doc(db, SCORECARD_MARKERS_COLLECTION, resultId), {
    yearMonth,
    markedAt: Timestamp.now(),
    markedBy: adminUser?.uid || null,
    markedByEmail: adminUser?.email || null
  })
}

/**
 * Unmark a single exam result
 * @param {string} resultId
 * @returns {Promise<void>}
 */
export const unmarkScorecardResult = async (resultId) => {
  if (!resultId) return
  await deleteDoc(doc(db, SCORECARD_MARKERS_COLLECTION, resultId))
}

/**
 * Mark or unmark many exam results in one batch
 * @param {string[]} resultIds
 * @param {boolean} shouldMark
 * @param {string} yearMonth - YYYY-MM (required when marking)
 * @returns {Promise<void>}
 */
export const setScorecardMarkersForResults = async (resultIds, shouldMark, yearMonth) => {
  if (!resultIds || resultIds.length === 0) return

  const adminUser = auth.currentUser
  const uniqueIds = [...new Set(resultIds)]

  // Firestore batches are limited to 500 operations
  for (let i = 0; i < uniqueIds.length; i += 450) {
    const chunk = uniqueIds.slice(i, i + 450)
    const batch = writeBatch(db)

    chunk.forEach((resultId) => {
      const markerRef = doc(db, SCORECARD_MARKERS_COLLECTION, resultId)
      if (shouldMark) {
        batch.set(markerRef, {
          yearMonth,
          markedAt: Timestamp.now(),
          markedBy: adminUser?.uid || null,
          markedByEmail: adminUser?.email || null
        })
      } else {
        batch.delete(markerRef)
      }
    })

    await batch.commit()
  }
}

/**
 * Get opened exam result IDs for a given month (submittedDate yearMonth)
 * @param {number} month - Month (0-11)
 * @param {number} year
 * @returns {Promise<string[]>}
 */
export const getScorecardOpenedByMonth = async (month, year) => {
  const yearMonth = formatScorecardYearMonth(month, year)
  const openedIds = []

  try {
    const q = query(
      collection(db, SCORECARD_OPENED_COLLECTION),
      where('yearMonth', '==', yearMonth)
    )
    const querySnapshot = await getDocs(q)
    querySnapshot.forEach((openedDoc) => {
      openedIds.push(openedDoc.id)
    })
  } catch (error) {
    console.error('Error getting scorecard opened docs:', error)
    throw error
  }

  return openedIds
}

/**
 * Mark an exam result as opened (shared across admins)
 * @param {string} resultId
 * @param {string} yearMonth - YYYY-MM from submittedDate
 * @returns {Promise<void>}
 */
export const openScorecardResult = async (resultId, yearMonth) => {
  if (!resultId || !yearMonth) return

  const adminUser = auth.currentUser
  await setDoc(doc(db, SCORECARD_OPENED_COLLECTION, resultId), {
    yearMonth,
    openedAt: Timestamp.now(),
    openedBy: adminUser?.uid || null,
    openedByEmail: adminUser?.email || null
  })
}

/**
 * Mark an exam result as unopened (removes New-cleared state)
 * @param {string} resultId
 * @returns {Promise<void>}
 */
export const unopenScorecardResult = async (resultId) => {
  if (!resultId) return
  await deleteDoc(doc(db, SCORECARD_OPENED_COLLECTION, resultId))
}

/**
 * Mark many exam results as opened in one batch
 * @param {string[]} resultIds
 * @param {string} yearMonth - YYYY-MM from submittedDate
 * @returns {Promise<void>}
 */
export const openScorecardResults = async (resultIds, yearMonth) => {
  if (!resultIds || resultIds.length === 0 || !yearMonth) return

  const adminUser = auth.currentUser
  const uniqueIds = [...new Set(resultIds)]

  for (let i = 0; i < uniqueIds.length; i += 450) {
    const chunk = uniqueIds.slice(i, i + 450)
    const batch = writeBatch(db)

    chunk.forEach((resultId) => {
      batch.set(doc(db, SCORECARD_OPENED_COLLECTION, resultId), {
        yearMonth,
        openedAt: Timestamp.now(),
        openedBy: adminUser?.uid || null,
        openedByEmail: adminUser?.email || null
      })
    })

    await batch.commit()
  }
}

/**
 * Get exam results for a specific month based on submittedDate
 * @param {number} month - Month (0-11, where 0 = January)
 * @param {number} year - Year (e.g., 2024)
 * @returns {Promise<Array>} Array of exam results
 */
export const getExamResultsBySubmittedMonth = async (month, year) => {
  const res = []

  const startDate = new Date(year, month, 1)
  startDate.setHours(0, 0, 0, 0)

  const endDate = new Date(year, month + 1, 0)
  endDate.setHours(23, 59, 59, 999)

  const startTimestamp = Timestamp.fromDate(startDate)
  const endTimestamp = Timestamp.fromDate(endDate)

  const q = query(
    collection(db, 'exam_results'),
    where('submittedDate', '>=', startTimestamp),
    where('submittedDate', '<=', endTimestamp),
    orderBy('submittedDate', 'desc')
  )

  try {
    const querySnapshot = await getDocs(q)
    querySnapshot.forEach((resultDoc) => {
      res.push({
        id: resultDoc.id,
        data: resultDoc.data()
      })
    })
  } catch (error) {
    console.error('Error getting exam results by submittedDate:', error)
    throw error
  }

  return res
}

/**
 * Get all CAAP subjects
 * @returns {Promise<Array>} Array of subjects
 */
export const getCaapSubjects = async () => {
  const querySnapshot = await getDocs(collection(db, 'reviewers', 'caap', 'subjects'))
  const subjects = []
  
  querySnapshot.forEach(doc => {
    subjects.push({
      id: doc.id,
      data: doc.data()
    })
  })
  
  return subjects
}

/**
 * Get all CAAP subtopics
 * @returns {Promise<Array>} Array of subtopics
 */
export const getCaapSubtopics = async () => {
  const querySnapshot = await getDocs(collection(db, 'reviewers', 'caap', 'subtopics'))
  const subtopics = []
  
  querySnapshot.forEach(doc => {
    subtopics.push({
      id: doc.id,
      data: doc.data()
    })
  })
  
  return subtopics
}

/**
 * Get exam results for a specific month based on examDate
 * @param {number} month - Month (0-11, where 0 = January)
 * @param {number} year - Year (e.g., 2024)
 * @returns {Promise<Array>} Array of exam results
 */
export const getExamResultsByMonth = async (month, year) => {
  const res = []
  
  // Get start and end dates for the month
  const startDate = new Date(year, month, 1)
  startDate.setHours(0, 0, 0, 0)
  
  const endDate = new Date(year, month + 1, 0)
  endDate.setHours(23, 59, 59, 999)
  
  // Convert to Firestore Timestamps
  const startTimestamp = Timestamp.fromDate(startDate)
  const endTimestamp = Timestamp.fromDate(endDate)
  
  // Construct Firestore query - use examDate for filtering
  const q = query(
    collection(db, 'exam_results'),
    where('examDate', '>=', startTimestamp),
    where('examDate', '<=', endTimestamp),
    orderBy('examDate', 'desc')
  )
  
  // Execute the query
  try {
    const querySnapshot = await getDocs(q)
    querySnapshot.forEach((doc) => {
      res.push({
        id: doc.id,
        data: doc.data()
      })
    })
  } catch (error) {
    console.error('Error getting exam results:', error)
    throw error
  }
  
  return res
}

/**
 * Get Knowledge Test Results (KTRs) for a specific user
 * @param {string} userId - User ID (used as document ID in exam_result_ktrs)
 * @returns {Promise<Array>} Array of KTRs or empty array if not found
 */
export const getUserKtrs = async (userId) => {
  if (!userId) return []
  
  try {
    const docSnap = await getDoc(doc(db, 'exam_result_ktrs', userId))
    return docSnap.exists() ? (docSnap.data().ktrs || []) : []
  } catch (error) {
    console.error('Error fetching user KTRs:', error)
    return []
  }
}

/**
 * Get all exam results for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of exam results
 */
export const getAllExamResultsByUserId = async (userId) => {
  if (!userId) return []
  
  const res = []
  
  try {
    const q = query(
      collection(db, 'exam_results'),
      where('userId', '==', userId),
      
    )
    
    const querySnapshot = await getDocs(q)
    querySnapshot.forEach((doc) => {
      res.push({
        id: doc.id,
        data: doc.data()
      })
    })
  } catch (error) {
    console.error('Error getting exam results by userId:', error)
    throw error
  }
  
  return res
}

/**
 * Get user CAAP data (activation_date, expiration_date, duration)
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} CAAP data object or null if not found
 */
export const getUserCaapData = async (userId) => {
  if (!userId) return null
  
  try {
    const docSnap = await getDoc(doc(db, 'users', userId))
    if (!docSnap.exists()) return null
    
    const userData = docSnap.data()
    const caapData = userData.caap_data
    
    if (!caapData) return null
    
    return {
      activation_date: caapData.activation_date || null,
      expiration_date: caapData.expiration_date || null,
      duration: caapData.duration || null
    }
  } catch (error) {
    console.error('Error fetching user CAAP data:', error)
    return null
  }
}

/** ScoreCard CAAP Knowledge reviewer id in transactions.subscriptions */
const CAAP_KNOWLEDGE_REVIEWER_ID = 'caap-knowledge'

/**
 * Grace days added to each confirmed transaction duration when reconstructing
 * prior subscription windows (purchase/confirm → activate lag).
 */
export const CAAP_TRANSACTION_DURATION_GRACE_DAYS = 5

/**
 * Convert Firestore Timestamp / Date / millis / numeric string to epoch ms.
 * @param {*} value
 * @returns {number|null}
 */
const toEpochMs = (value) => {
  if (value == null) return null
  if (typeof value.toMillis === 'function') return value.toMillis()
  if (typeof value.seconds === 'number') return value.seconds * 1000
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const asNumber = Number(value)
    if (Number.isFinite(asNumber)) return asNumber
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

/**
 * Confirmed CAAP Knowledge subscription periods from `transactions` for a user.
 * Window: [confirmation_date || purchase_date, start + duration_in_days + grace].
 * Used by ScoreCard "Filter by CAAP Dates" so renewals don't hide exams that
 * were valid under a prior purchase (ENG-15).
 *
 * @param {string} userId
 * @returns {Promise<Array<{ transactionId: string, startMs: number, endMs: number, durationDays: number }>>}
 */
export const getUserConfirmedCaapKnowledgePeriods = async (userId) => {
  if (!userId) return []

  try {
    const q = query(
      collection(db, 'transactions'),
      where('user_id', '==', userId),
      where('status', '==', 'confirmed')
    )
    const snapshot = await getDocs(q)
    const periods = []

    snapshot.forEach((docSnap) => {
      const data = docSnap.data()
      const subscriptions = Array.isArray(data.subscriptions) ? data.subscriptions : []
      const caapSub = subscriptions.find(
        (sub) => sub && sub.reviewer_id === CAAP_KNOWLEDGE_REVIEWER_ID
      )
      if (!caapSub) return

      const durationDays = Number(caapSub.duration_in_days)
      if (!Number.isFinite(durationDays) || durationDays <= 0) return

      const startMs =
        toEpochMs(data.confirmation_date) ?? toEpochMs(data.purchase_date)
      if (startMs == null) return

      const endMs =
        startMs +
        (durationDays + CAAP_TRANSACTION_DURATION_GRACE_DAYS) * 24 * 60 * 60 * 1000

      periods.push({
        transactionId: docSnap.id,
        startMs,
        endMs,
        durationDays
      })
    })

    return periods
  } catch (error) {
    console.error('Error fetching confirmed CAAP Knowledge transaction periods:', error)
    return []
  }
}

